import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  BUYER_INTEREST_RANGE_OPTIONS,
  BUYER_INTEREST_SORT_OPTIONS,
  DEFAULT_BUYER_INTEREST_RANGE,
  DEMAND_SIGNAL_LABEL_KEYS,
  trackBuyerInterestEvent,
} from "../../constants/automapDealerBuyerInterestConstants";
import { automapDealerPortalPath } from "../../constants/automapDealerPortalRoutes";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useCurrency } from "../../intl";
import { fetchDealerBuyerInterestListings, postDealerBuyerInterestEvent } from "../../services/automapDealerBuyerInterestApi";

export default function DealerInventoryDemandPage() {
  const { dealerId } = useOutletContext();
  const { t, formatListingStatus } = useDealerPortalIntl();
  const { formatMoney } = useCurrency();
  const [range, setRange] = useState(DEFAULT_BUYER_INTEREST_RANGE);
  const [sort, setSort] = useState("likes");
  const [status, setStatus] = useState("active");
  const [page, setPage] = useState(1);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerBuyerInterestListings(dealerId, { range, sort, status, page, limit: 25 });
      setPayload(data);
      trackBuyerInterestEvent("dealer_buyer_interest_viewed", { dealerId, range, sort, section: "demand_panel" });
      postDealerBuyerInterestEvent(dealerId, "dealer_buyer_interest_viewed", { range, sort }).catch(() => {});
    } catch (err) {
      setPayload(null);
      setError(err.message || t("buyerInterest.loadError"));
    } finally {
      setLoading(false);
    }
  }, [dealerId, range, sort, status, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRangeChange = (next) => {
    setRange(next);
    setPage(1);
    trackBuyerInterestEvent("dealer_buyer_interest_range_changed", { dealerId, range: next });
    postDealerBuyerInterestEvent(dealerId, "dealer_buyer_interest_range_changed", { range: next }).catch(() => {});
  };

  const handleSortChange = (next) => {
    setSort(next);
    setPage(1);
    trackBuyerInterestEvent("dealer_buyer_interest_sort_changed", { dealerId, sort: next });
    postDealerBuyerInterestEvent(dealerId, "dealer_buyer_interest_sort_changed", { sort: next }).catch(() => {});
  };

  const items = payload?.items || [];
  const pagination = payload?.pagination || { page: 1, totalPages: 1, total: 0 };

  return (
    <div>
      <div className="dealer-inventory-toolbar">
        <div>
          <h2>{t("buyerInterest.demandPanelTitle")}</h2>
          <p className="muted">{t("buyerInterest.demandPanelIntro")}</p>
        </div>
        <Link className="dealer-portal-btn" to={automapDealerPortalPath("inventario")}>
          {t("buyerInterest.backToInventory")}
        </Link>
      </div>

      <div className="dealer-inventory-toolbar">
        <label>
          {t("common.period")}
          <select value={range} onChange={(e) => handleRangeChange(e.target.value)}>
            {BUYER_INTEREST_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("buyerInterest.sortLabel")}
          <select value={sort} onChange={(e) => handleSortChange(e.target.value)}>
            {BUYER_INTEREST_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("buyerInterest.statusLabel")}
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="active">{t("common.active")}</option>
            <option value="all">{t("buyerInterest.statusAll")}</option>
          </select>
        </label>
      </div>

      <p className="dealer-panel__footnote muted">{t("buyerInterest.privacyNote")}</p>

      {loading ? <p className="dealer-state-box">{t("common.loading")}</p> : null}
      {error ? <p className="dealer-state-box">{error}</p> : null}

      {!loading && !error && items.length === 0 ? (
        <section className="dealer-panel">
          <h3>{t("buyerInterest.emptyInterestTitle")}</h3>
          <p className="muted">{t("buyerInterest.emptyInterestBody")}</p>
        </section>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <section className="dealer-state-box">
          <table className="dealer-inventory-table">
            <thead>
              <tr>
                <th>{t("buyerInterest.colVehicle")}</th>
                <th>{t("buyerInterest.colPrice")}</th>
                <th>{t("buyerInterest.likes")}</th>
                <th>{t("buyerInterest.compares")}</th>
                <th>{t("buyerInterest.watchers")}</th>
                <th>{t("buyerInterest.colDemand")}</th>
                <th>{t("buyerInterest.colStatus")}</th>
                <th>{t("buyerInterest.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.listingId}>
                  <td>
                    <div className="dealer-demand-vehicle">
                      {row.thumbnailUrl ? (
                        <img src={row.thumbnailUrl} alt="" className="dealer-top-listing__thumb" />
                      ) : (
                        <div className="dealer-top-listing__thumb dealer-top-listing__thumb--empty" />
                      )}
                      <span>{row.title}</span>
                    </div>
                  </td>
                  <td>{formatMoney(row.priceCop)}</td>
                  <td>{row.likeCountLabel ?? row.likeCount ?? "0"}</td>
                  <td>{row.compareCountLabel ?? row.compareCount ?? "0"}</td>
                  <td>{row.watchCountLabel ?? row.watchCount ?? "0"}</td>
                  <td>{t(DEMAND_SIGNAL_LABEL_KEYS[row.demandSignal] || DEMAND_SIGNAL_LABEL_KEYS.no_data)}</td>
                  <td>{formatListingStatus(row.status)}</td>
                  <td>
                    <Link
                      to={automapDealerPortalPath(`inventario/${row.listingId}`)}
                      onClick={() =>
                        trackBuyerInterestEvent("dealer_buyer_interest_listing_clicked", {
                          dealerId,
                          listingId: row.listingId,
                        })
                      }
                    >
                      {t("common.view")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.totalPages > 1 ? (
            <div className="dealer-inline-actions">
              <button
                type="button"
                className="dealer-portal-btn"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                {t("buyerInterest.prevPage")}
              </button>
              <span className="muted">
                {page} / {pagination.totalPages}
              </span>
              <button
                type="button"
                className="dealer-portal-btn"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                {t("buyerInterest.nextPage")}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
